import {
  useInfiniteQuery as useInfiniteQueryColada,
  type UseInfiniteQueryData,
} from '@pinia/colada';
import { computed } from 'vue';

/** Pass as `placeholderData` to keep prior pages visible across key changes. */
export const keepPreviousData = <T>(previousData: T): T => previousData;

/**
 * Wrapper around Pinia Colada's useInfiniteQuery that adds:
 * - `pagesData`: Computed array of all pages
 * - `infiniteScrollProps`: Props for DataGrid infinite scroll integration
 *
 * @see docs/pinia-and-pinia-colada-guidelines.md for usage guidance
 *
 * @example
 * const { pagesData, infiniteScrollProps, ...rest } = useInfiniteQuery({
 *   key: () => ['items', brandId],
 *   query: async ({ pageParam }) => fetchItems({ offset: pageParam * PAGE_SIZE }),
 *   initialPageParam: 0,
 *   getNextPageParam: (lastPage, _, lastPageParam) =>
 *     lastPage.length === PAGE_SIZE ? lastPageParam + 1 : null,
 * });
 *
 * // Flatten pages for display
 * const items = computed(() => pagesData.value.flat());
 *
 * // Use with DataGrid
 * <DataGrid :value="items" infinite-scroll v-bind="infiniteScrollProps" />
 *
 * @see {@link keepPreviousData} to keep prior pages visible across key changes.
 */
export function useInfiniteQuery<
  TData,
  TError = Error,
  TPageParam = unknown,
  TDataInitial extends UseInfiniteQueryData<TData, TPageParam> | undefined = undefined,
>(
  queryOptions: Parameters<
    typeof useInfiniteQueryColada<TData, TError, TPageParam, TDataInitial>
  >[0],
) {
  const result = useInfiniteQueryColada(queryOptions);

  return {
    ...result,
    /** All pages as an array - use .flat() to get a single array of items */
    pagesData: computed(() => result.state.value?.data?.pages ?? []),
    /** Props for DataGrid infinite scroll integration */
    infiniteScrollProps: {
      onLoadMore: async () => {
        await result.loadNextPage();
      },
      canLoadMore: () => !result.error.value && result.hasNextPage.value,
      // Note: This composable fetches the first page immediately.
      // To avoid superfluous call to `loadNextPage` when using <DataGrid infiniteScroll>,
      // it is recommended to set `loadOnMount` to false.
      loadOnMount: false,
    },
  };
}
